import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  acceptMyShift,
  acknowledgeMyShift,
  doneMyTask,
  seenMyShift,
  declineMyShift,
  myPhoneSharingQuery,
  myShiftsQuery,
  setMyPhoneSharing,
  type MyShift,
} from '../api/crew.ts';
import { acceptMyOffer, declineMyOffer, myOffersQuery, type MyOffer } from '../api/offers.ts';
import { dayLabel } from '../api/quotes.ts';
import { crewSafetyDocumentQuery, signMine } from '../api/safety.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { RiskTable } from './safety.tsx';
import { MyTimeOff } from './my-time-off.tsx';

// CB-REQ CRW-08 and CRW-13 at v1.964, crew part 2: a freelancer's own shifts, on their phone, in
// time order across every job. ADO-01: legible without instruction, at 0500, with no one to ask —
// so each card says in words what is being asked of them, and the one thing to press is the
// primary button. Nothing here can show a rate or office notes: the type has neither (INV-10).
// CRW-08 at v1.979, crew part 3b: once the office confirms a shift, who else is on it, the gear,
// and the numbers to call — the lead's and the office's always, others only if they share theirs.
// Crew part 4A (CRW-11 and OPS-02 at v1.996): a shift is their booked time — where they report and
// who they ask, its own — and the work is its tasks, each marked Done here.

export function MyShiftsScreen() {
  const shifts = useQuery(myShiftsQuery);
  // CRW-04 at v1.994: offers with places made to me; one I took is a shift below instead.
  const offers = useQuery(myOffersQuery);
  const offered = (offers.data ?? []).filter((o) => !o.myShiftId);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader kicker="Crew" title="My shifts" />
      <PhoneSharingSwitch />
      {shifts.isPending ? <p className="text-muted">Loading…</p> : null}
      {shifts.isError ? (
        <p role="alert" className="alert">
          {shifts.error.message}
        </p>
      ) : null}
      {offered.length > 0 ? (
        <section aria-labelledby="my-offers" className="flex flex-col gap-3">
          <h2 id="my-offers" className="section-title">
            Offers
          </h2>
          <ul className="flex flex-col gap-3">
            {offered.map((o) => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </ul>
        </section>
      ) : null}
      {shifts.data?.length === 0 && offered.length === 0 ? (
        <p className="text-ink-soft">
          Nothing offered to you yet. When the office offers you a shift it will be here.
        </p>
      ) : null}
      {shifts.data && shifts.data.length > 0 ? (
        <ol className="flex flex-col gap-6">
          {byDay(shifts.data).map(([day, list]) => (
            <li key={day} className="flex flex-col gap-3">
              <h2 className="section-title">{dayLabel(day)}</h2>
              <ul className="flex flex-col gap-3">
                {list.map((s) => (
                  <ShiftCard key={s.id} shift={s} />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      ) : null}
      {/* CRW-02 at v1.9106: their own unavailability and leave, where their company allows. */}
      <MyTimeOff />
    </div>
  );
}

/** Grouped by the local day each shift starts on, in the order they come. */
function byDay(shifts: readonly MyShift[]): Array<[string, MyShift[]]> {
  const days = new Map<string, MyShift[]>();
  for (const s of shifts) {
    const day = s.localDay;
    days.set(day, [...(days.get(day) ?? []), s]);
  }
  return [...days];
}

const time = (local: string) => local.slice(11, 16);

function ShiftCard({ shift }: { shift: MyShift }) {
  const queryClient = useQueryClient();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setDeclining(false);
      await queryClient.invalidateQueries({ queryKey: myShiftsQuery.queryKey });
    },
  });
  const s = shift;
  const venue = s.venue;
  const where = venue
    ? `${venue.name}${venue.address ? `, ${venue.address}` : ''}`
    : (s.location ?? '');
  const ask = askOf(s);
  const leading = s.leads.filter((l) => l.you);
  const leads = s.leads.filter((l) => !l.you);

  return (
    <li className="panel flex flex-col gap-3 p-4">
      {ask ? (
        <p
          className={
            (s.changeOutcome && s.changeOutcome !== 'notify') ||
            s.state === 'cancelled' ||
            s.bookedFor?.cancelled
              ? 'alert'
              : 'notice'
          }
        >
          {ask}
        </p>
      ) : null}
      {/* CMP-02 / CMP-04 at v1.967: what to read and sign before the day. */}
      {s.toSign.length > 0 && s.state !== 'cancelled' ? <ToSign shift={s} /> : null}
      {/* CMP-07 at v1.965: what they still need, so they can sort it before the day. */}
      {s.missing.length > 0 && s.state !== 'cancelled' ? (
        <p className="alert">
          You still need: {s.missing.join(', ')}. Send the office a photo when you have it.
        </p>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className="text-lg font-semibold">
          {/* CRW-11 at v1.996: booked for the day; where they report comes later. */}
          {s.held ? `Booked — venue to come${s.role ? ` · ${s.role}` : ''}` : (s.role ?? 'Booked')}
          {leading.length > 0 ? <span className="tag tag-accent ml-2">You lead</span> : null}
          {/* CRW-16 at v1.978: the office has not confirmed it, so it may still change. */}
          {s.provisional && !s.held ? <span className="tag ml-2">Provisional</span> : null}
        </p>
        {s.localStart && s.localEnd ? (
          <p>
            <span className="font-semibold">
              {time(s.localStart)} – {time(s.localEnd)}
            </span>
            {s.localCallAt && s.localCallAt !== s.localStart
              ? ` · call ${time(s.localCallAt)}`
              : ''}
          </p>
        ) : null}
        {s.bookedFor ? (
          <p className="text-ink-soft">
            {s.bookedFor.name} · {s.bookedFor.reference}
          </p>
        ) : null}
        {where ? <p className="text-ink-soft">{where}</p> : null}
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        {s.meetingPoint ? <Row label="Meet at">{s.meetingPoint}</Row> : null}
        {s.directToSite || s.directFromSite ? (
          <Row label="Travel">
            {[
              s.directToSite ? 'straight to site' : null,
              s.directFromSite ? 'home from site' : null,
            ]
              .filter(Boolean)
              .join(', ')}
          </Row>
        ) : null}
        {s.dressCode ? <Row label="Wear">{s.dressCode}</Row> : null}
        {/* CRW-16 at v1.996: who to ask on the day; their number always shown, as a lead's is. */}
        {s.contact ? (
          <Row label="Ask">
            {s.contact.name}
            {s.contact.phone ? (
              <>
                {' '}
                <Tel number={s.contact.phone} />
              </>
            ) : null}
          </Row>
        ) : null}
        {leads.map((l) => (
          <Row key={l.job} label="Lead">
            {l.name}
            {s.leads.length > 1 ? <span className="text-ink-soft"> · {l.job}</span> : null}
            {l.phone ? (
              <>
                {' '}
                <Tel number={l.phone} />
              </>
            ) : null}
          </Row>
        ))}
        {/* CRW-08 at v1.979: the office's number, on every confirmed shift. */}
        {s.confirmed ? (
          <Row label="Office">
            {s.officePhone ? (
              <Tel number={s.officePhone} />
            ) : (
              <span className="text-ink-soft">No office number set</span>
            )}
          </Row>
        ) : null}
        {s.bookedFor?.siteContactName || s.bookedFor?.siteContactPhone ? (
          <Row label="On site">
            {s.bookedFor.siteContactName ?? ''}
            {s.bookedFor.siteContactPhone ? (
              <>
                {' '}
                <Tel number={s.bookedFor.siteContactPhone} />
              </>
            ) : null}
          </Row>
        ) : null}
        {s.bookedFor?.loadNotes ? <Row label="Loading">{s.bookedFor.loadNotes}</Row> : null}
        {/* VEN-02 and VEN-07 at v1.971: getting in, parking, and what to do in an emergency. */}
        {venue?.accessNotes ? <Row label="Getting in">{venue.accessNotes}</Row> : null}
        {venue?.parkingNotes ? <Row label="Parking">{venue.parkingNotes}</Row> : null}
        {venue?.musterPoint ? (
          <Row label="Emergency">
            Gather at {venue.musterPoint}
            {venue.emergencyContact ? (
              <>
                {' '}
                · {venue.emergencyContact.name}
                {venue.emergencyContact.phone ? (
                  <>
                    {' '}
                    <Tel number={venue.emergencyContact.phone} />
                  </>
                ) : null}
              </>
            ) : null}
          </Row>
        ) : venue?.emergencyContact ? (
          <Row label="Emergency">
            {venue.emergencyContact.name}
            {venue.emergencyContact.phone ? ` ${venue.emergencyContact.phone}` : ''}
          </Row>
        ) : null}
        {s.crewNotes ? <Row label="Notes">{s.crewNotes}</Row> : null}
      </dl>

      {/* CRW-08 at v1.979: the lead's number is shown to the job's crew, whatever they chose. */}
      {leading.length > 0 && s.confirmed ? (
        <p className="notice text-sm">You’re leading: your number is shown to this job’s crew.</p>
      ) : null}

      {/* OPS-02 at v1.996: the work, from a few days out; Done on each. */}
      {s.tasks.length > 0 ? <Tasks shift={s} /> : null}

      {s.crew.length > 0 ? (
        <section aria-label="Who else is on" className="flex flex-col gap-1 text-sm">
          <p className="font-semibold">Who else is on</p>
          <ul className="flex flex-col gap-1">
            {s.crew.map((c, n) => (
              <li key={n}>
                <span className="font-semibold">{c.name}</span>, {c.role} · {time(c.localStart)} –{' '}
                {time(c.localEnd)}
                {c.isLead ? <span className="tag tag-accent ml-2">Lead</span> : null}
                {c.phone ? (
                  <>
                    {' '}
                    <Tel number={c.phone} />
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {s.gear.length > 0 ? (
        <section aria-label="Gear" className="flex flex-col gap-1 text-sm">
          <p className="font-semibold">Gear</p>
          <ul className="flex flex-col gap-1">
            {s.gear.map((g, n) => (
              <li key={n}>
                {g.quantity} × {g.description}
                {g.room ? <span className="text-ink-soft"> · {g.room}</span> : null}
                <span className="block text-ink-soft">
                  {g.serials.length > 0 ? g.serials.join(', ') : 'Not yet allocated'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* VEN-09 at v1.996: the standing instructions, among what they confirm they've read. */}
      {s.instructions.length > 0 ? (
        <section aria-label="Instructions" className="flex flex-col gap-1 text-sm">
          <p className="font-semibold">Instructions</p>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {s.instructions.map((i, n) => (
              <li key={n} className="whitespace-pre-line">
                {/* CRW-14 at v1.975: new or changed since they last said yes. */}
                {i.changed ? <span className="tag tag-accent mr-2">Changed</span> : null}
                {i.room ? <span className="font-semibold">{i.room}: </span> : null}
                {i.text}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {s.state === 'offered' && !declining ? (
        <div className="flex flex-wrap gap-2">
          <Button disabled={act.isPending} onClick={() => act.mutate(() => acceptMyShift(s.id))}>
            Accept
          </Button>
          <Button variant="quiet" onClick={() => setDeclining(true)}>
            Decline offer…
          </Button>
        </div>
      ) : null}
      {declining ? (
        <div className="flex flex-col gap-2">
          <Field
            label="Why (optional — it helps the office)"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={act.isPending}
              onClick={() => act.mutate(() => declineMyShift(s.id, reason.trim() || null))}
            >
              Decline
            </Button>
            <Button variant="quiet" onClick={() => setDeclining(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : null}
      {s.state === 'accepted' && s.confirmed && !s.acknowledgedAt ? (
        <Button
          className="self-start"
          disabled={act.isPending}
          onClick={() => act.mutate(() => acknowledgeMyShift(s.id))}
        >
          Confirm
        </Button>
      ) : null}
      {s.changeOutcome === 'notify' ? (
        <Button
          className="self-start"
          disabled={act.isPending}
          onClick={() => act.mutate(() => seenMyShift(s.id))}
        >
          Got it
        </Button>
      ) : null}
      {s.state === 'accepted' && s.acknowledgedAt && s.changeOutcome !== 'notify' ? (
        <p className="text-sm text-ink-soft">You’ve confirmed this shift.</p>
      ) : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </li>
  );
}

/** What this card is asking of them, in words (ADO-01), or nothing when there is nothing to do. */
function askOf(s: MyShift): string | null {
  // CRW-11 at v1.980: a held day released, with the office's reason.
  if (s.state === 'cancelled' && s.held) {
    return `The office released this day${s.cancelReason ? `: ${s.cancelReason}` : '.'}`;
  }
  if (s.state === 'cancelled') return 'This shift has been cancelled.';
  if (s.bookedFor?.cancelled) return 'This job has been cancelled. The office will be in touch.';
  if (s.changeOutcome === 'reaccept') {
    return 'This shift has changed a lot since you accepted it. Check it, then accept it again.';
  }
  if (s.changeOutcome === 'notify') {
    return `Since you confirmed, ${changeList(s.changes)} changed. Have a look, then tap Got it.`;
  }
  if (s.changeOutcome === 'reconfirm') {
    return s.instructionsChanged
      ? 'The instructions have changed. Read them, then confirm again.'
      : 'This shift has changed since you confirmed it. Check the details, then confirm again.';
  }
  if (s.state === 'offered' && s.held) {
    return 'You’ve been offered this day. Where you’ll be comes closer to the day.';
  }
  if (s.state === 'offered') return 'You’ve been offered this shift.';
  if (s.state === 'accepted' && s.held) {
    return 'You’re booked for this day. The office will say where closer to the day.';
  }
  if (s.state === 'accepted' && !s.confirmed) {
    return 'You’ve accepted. The office will confirm the details closer to the day.';
  }
  if (s.state === 'accepted' && !s.acknowledgedAt) {
    return 'You’ve accepted. Read the details, then confirm you have.';
  }
  if (s.state === 'declined') return 'You declined this shift.';
  return null;
}

/** CRW-14 at v1.975: "the meeting point and the venue’s instructions", in plain words. */
const CHANGE_WORDS: Record<string, string> = {
  times: 'the times',
  where: 'where it is',
  role: 'the role',
  'meeting point': 'the meeting point',
  travel: 'the travel',
  'dress code': 'what to wear',
  notes: 'the notes',
  instructions: 'the instructions',
  contact: 'who to ask',
  'task added': 'your tasks',
};
function changeList(changes: readonly string[]): string {
  const words = changes.map((c) => CHANGE_WORDS[c] ?? c);
  if (words.length === 0) return 'something';
  if (words.length === 1) return words[0]!;
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
}

/** OPS-02 at v1.996: their tasks on this shift, each with Done; no office notes reach them. */
function Tasks({ shift }: { shift: MyShift }) {
  const queryClient = useQueryClient();
  const done = useMutation({
    mutationFn: doneMyTask,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myShiftsQuery.queryKey });
    },
  });
  return (
    <section aria-label="Tasks" className="flex flex-col gap-1 text-sm">
      <p className="font-semibold">Tasks</p>
      <ul className="flex flex-col gap-2">
        {shift.tasks.map((t) => (
          <li key={t.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className={`font-semibold ${t.status === 'done' ? 'line-through' : ''}`}>
              {t.description}
            </span>
            <span className="text-ink-soft">
              {time(t.localStart)} – {time(t.localEnd)}
              {t.role ? ` · ${t.role}` : ''}
              {t.room ? ` · ${t.room}` : ''}
            </span>
            {t.crewNotes ? <span className="block w-full">{t.crewNotes}</span> : null}
            {t.status === 'done' ? (
              <span className="tag">Done</span>
            ) : shift.state === 'accepted' ? (
              <Button variant="quiet" disabled={done.isPending} onClick={() => done.mutate(t.id)}>
                Done<span className="sr-only"> — {t.description}</span>
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {done.isError ? (
        <p role="alert" className="alert">
          {done.error.message}
        </p>
      ) : null}
    </section>
  );
}

function Tel({ number }: { number: string }) {
  return (
    <a className="link" href={`tel:${number.replace(/\s/g, '')}`}>
      {number}
    </a>
  );
}

/**
 * CRW-08 and CLI-07 at v1.979: whether their number is shown to the other crew on their jobs.
 * Off until they say so; each change is recorded with the words shown here.
 */
function PhoneSharingSwitch() {
  const queryClient = useQueryClient();
  const sharing = useQuery(myPhoneSharingQuery);
  const save = useMutation({
    mutationFn: setMyPhoneSharing,
    onSuccess: async (data) => {
      queryClient.setQueryData(myPhoneSharingQuery.queryKey, data);
      await queryClient.invalidateQueries({ queryKey: myShiftsQuery.queryKey });
    },
  });
  if (!sharing.data) return null;
  const p = sharing.data;
  return (
    <section aria-label="Your phone number" className="panel flex flex-col gap-2 p-4 text-sm">
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={p.shared}
          disabled={save.isPending || !p.phone}
          onChange={(e) => save.mutate(e.currentTarget.checked)}
        />
        <span>
          {p.wording}
          {p.phone ? (
            <span className="block text-ink-soft">Your number: {p.phone}</span>
          ) : (
            <span className="block text-ink-soft">
              The office has no number for you. Ask them to add it.
            </span>
          )}
          {p.onBehalf && p.recordedBy ? (
            <span className="block text-ink-soft">
              Set by {p.recordedBy} for you{p.note ? ` (${p.note})` : ''}.
            </span>
          ) : null}
        </span>
      </label>
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-soft">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** CMP-02 at v1.967: each document to sign, opened in place, read, then signed. */
function ToSign({ shift }: { shift: MyShift }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="notice flex flex-col gap-2">
      <p className="font-semibold">Read and sign before you start</p>
      <ul className="flex flex-col gap-2">
        {shift.toSign.map((d) => (
          <li key={d.documentId} className="flex flex-col gap-2">
            <span className="flex flex-wrap items-center gap-2">
              {d.title}
              <span className="text-sm text-ink-soft">
                {d.kind === 'swms' ? 'SWMS' : 'Risk assessment'}, version {d.versionNumber}
              </span>
              {open !== d.documentId ? (
                <button type="button" className="link" onClick={() => setOpen(d.documentId)}>
                  Read<span className="sr-only"> {d.title}</span>
                </button>
              ) : null}
            </span>
            {open === d.documentId ? (
              <SignSheet
                shiftId={shift.id}
                documentId={d.documentId}
                onClose={() => setOpen(null)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SignSheet({
  shiftId,
  documentId,
  onClose,
}: {
  shiftId: string;
  documentId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const doc = useQuery(crewSafetyDocumentQuery(shiftId, documentId));
  const sign = useMutation({
    mutationFn: () => signMine(shiftId, documentId),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: myShiftsQuery.queryKey });
    },
  });
  if (doc.isPending) return <p className="text-sm text-muted">Loading…</p>;
  if (doc.isError) {
    return (
      <p role="alert" className="alert">
        {doc.error.message}
      </p>
    );
  }
  return (
    <section
      aria-label={`${doc.data.title}, version ${doc.data.versionNumber}`}
      className="flex flex-col gap-3"
    >
      <RiskTable rows={doc.data.rows} />
      {sign.isError ? (
        <p role="alert" className="alert">
          {sign.error.message}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={sign.isPending} onClick={() => sign.mutate()}>
          Sign<span className="sr-only"> this SWMS</span>
        </Button>
        <Button type="button" variant="quiet" onClick={onClose}>
          Close
        </Button>
      </div>
    </section>
  );
}

/**
 * CRW-04 at v1.994 – v1.995: an offer of places made to me. The first to accept take them, so it
 * may be taken by the time I look; a decline is final until the office offers it again. Shown as
 * the company's pre-confirmation view allows. Never a rate (INV-10).
 */
function OfferCard({ offer }: { offer: MyOffer }) {
  const queryClient = useQueryClient();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setDeclining(false);
      await queryClient.invalidateQueries({ queryKey: myOffersQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: myShiftsQuery.queryKey });
    },
  });
  const o = offer;
  const left = o.places - o.taken;
  return (
    <li className="panel flex flex-col gap-3 p-4" data-offer={o.id}>
      <div className="flex flex-col gap-1">
        <p className="text-lg font-semibold">
          {o.role ?? (o.held ? 'Booked — venue to come' : 'Booked')}
          <span className="tag ml-2">{left > 0 ? `${left} of ${o.places} left` : 'Taken'}</span>
          {o.declined ? <span className="tag ml-2">Declined</span> : null}
        </p>
        <p>
          <span className="font-semibold">{dayLabel(o.window.localStart.slice(0, 10))}</span>{' '}
          {time(o.window.localStart)} – {time(o.window.localEnd)}
          {o.localCallAt && o.localCallAt !== o.window.localStart
            ? ` · call ${time(o.localCallAt)}`
            : ''}
        </p>
        {o.bookedFor || o.place ? (
          <p className="text-ink-soft">
            {[o.bookedFor?.name, o.place].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        {o.meetingPoint ? <p className="text-sm">Meet: {o.meetingPoint}</p> : null}
        {o.crewNotes ? <p className="text-sm">{o.crewNotes}</p> : null}
      </div>
      {o.open && !o.declined && !declining ? (
        <div className="flex flex-wrap gap-2">
          <Button disabled={act.isPending} onClick={() => act.mutate(() => acceptMyOffer(o.id))}>
            Accept
          </Button>
          <Button variant="quiet" onClick={() => setDeclining(true)}>
            Decline…
          </Button>
        </div>
      ) : null}
      {declining ? (
        <div className="flex flex-col gap-2">
          <Field
            label="Why (optional)"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={act.isPending}
              onClick={() => act.mutate(() => declineMyOffer(o.id, reason.trim() || null))}
            >
              Decline
            </Button>
            <Button variant="quiet" onClick={() => setDeclining(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : null}
      {act.error ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </li>
  );
}
